import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';

const baseControlStyle = (isOpen, disabled, hasError) => ({
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  border: hasError ? '1px solid var(--red)' : isOpen ? '1px solid #3b82f6' : '1px solid var(--border, #d1d5db)',
  background: disabled ? '#f8fafc' : 'var(--bg, #ffffff)',
  color: 'var(--text, #0f172a)',
  fontSize: '14px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  minHeight: '48px',
  boxShadow: isOpen ? '0 0 0 4px rgba(59, 130, 246, 0.08)' : 'none'
});

const DROPDOWN_Z_INDEX = 9999;

const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14)',
  zIndex: DROPDOWN_Z_INDEX,
  overflow: 'hidden',
};

const selectAllBarStyle = {
  display: 'flex',
  gap: '8px',
  padding: '8px 10px',
  borderBottom: '1px solid #e2e8f0',
  background: '#f8fafc',
};

const selectAllBtnStyle = {
  flex: 1,
  padding: '6px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  background: '#ffffff',
  color: '#334155',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
};

export const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Type to search...',
  disabled = false,
  error = false,
  noOptionsText = 'No options found',
  disabledText = 'Field is disabled',
  allowClear = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!query.trim()) {
      return options;
    }

    const normalizedQuery = query.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [options, query]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div
      ref={wrapperRef}
      className={`searchable-select-wrapper${isOpen ? ' is-open' : ''}`}
      style={{ position: 'relative', width: '100%', zIndex: isOpen ? DROPDOWN_Z_INDEX : 'auto' }}
    >
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((current) => !current)}
        style={baseControlStyle(isOpen, disabled, error)}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left', flex: 1, color: selectedOption ? 'var(--text, #0f172a)' : '#94a3b8' }}>
          {selectedOption?.label || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {allowClear && selectedOption && !disabled && (
            <span
              onClick={(event) => {
                event.stopPropagation();
                onChange('');
                setQuery('');
                setIsOpen(false);
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}
            >
              <Icon name="x" size={14} />
            </span>
          )}
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={18} style={{ color: '#64748b' }} />
        </div>
      </button>

      {isOpen && (
        <div style={dropdownStyle}>
          <div style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={disabled ? disabledText : searchPlaceholder}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '8px' }}>
            {!filteredOptions.length ? (
              <div style={{ padding: '12px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>
                {disabled ? disabledText : noOptionsText}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 'none',
                      background: isSelected ? '#eff6ff' : 'transparent',
                      color: isSelected ? '#2563eb' : '#334155',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isSelected ? '600' : '400'
                    }}
                  >
                    {option.label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const SearchableMultiSelect = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  disabled = false,
  noOptionsText = 'No options found',
  disabledText = 'Field is disabled',
  showSelectAll = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!query.trim()) {
      return options;
    }

    const normalizedQuery = query.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [options, query]);

  const getSelectedLabels = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    const labels = options.filter((option) => selectedValues.includes(option.value)).map((option) => option.label);
    if (labels.length <= 2) {
      return labels.join(', ');
    }
    return `${labels.length} items selected`;
  };

  const toggleOption = (optionValue) => {
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((value) => value !== optionValue));
      return;
    }

    onChange([...selectedValues, optionValue]);
  };

  const handleSelectAll = () => {
    onChange(options.map((option) => option.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const allSelected = options.length > 0 && options.every((option) => selectedValues.includes(option.value));

  return (
    <div
      ref={wrapperRef}
      className={`searchable-select-wrapper${isOpen ? ' is-open' : ''}`}
      style={{ position: 'relative', width: '100%', zIndex: isOpen ? DROPDOWN_Z_INDEX : 'auto' }}
    >
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((current) => !current)}
        style={baseControlStyle(isOpen, disabled, false)}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left', flex: 1, color: selectedValues.length ? 'var(--text, #0f172a)' : '#94a3b8' }}>
          {getSelectedLabels()}
        </span>
        <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={18} style={{ color: '#64748b', flexShrink: 0 }} />
      </button>

      {isOpen && (
        <div style={dropdownStyle}>
          <div style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={disabled ? disabledText : 'Type to filter...'}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {showSelectAll && !disabled && options.length > 0 && (
            <div style={selectAllBarStyle}>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={allSelected}
                style={{
                  ...selectAllBtnStyle,
                  opacity: allSelected ? 0.5 : 1,
                  cursor: allSelected ? 'not-allowed' : 'pointer',
                  color: '#2563eb',
                  borderColor: '#bfdbfe',
                  background: '#eff6ff',
                }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                disabled={selectedValues.length === 0}
                style={{
                  ...selectAllBtnStyle,
                  opacity: selectedValues.length === 0 ? 0.5 : 1,
                  cursor: selectedValues.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                Clear All
              </button>
            </div>
          )}

          <div style={{ maxHeight: '260px', overflowY: 'auto', padding: '8px' }}>
            {!filteredOptions.length ? (
              <div style={{ padding: '12px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>
                {disabled ? disabledText : noOptionsText}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => toggleOption(option.value)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      border: 'none',
                      background: isSelected ? '#eff6ff' : 'transparent',
                      color: isSelected ? '#2563eb' : '#334155',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: isSelected ? '600' : '400',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '5px',
                      border: isSelected ? 'none' : '1px solid #cbd5e1',
                      background: isSelected ? '#3b82f6' : '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {isSelected && <Icon name="check" size={12} style={{ color: '#ffffff' }} />}
                    </div>
                    <span>{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
